'use client';

import React, { createContext, useContext, useState, ReactNode, useCallback, useRef, useEffect } from 'react';
import { WebSocketService, ConnectionState, getSession, fetchNewSessionId } from '@akavirtual/deai-web-core';

// Define the minimal socket interface
interface SocketContextType {
  sessionId: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  isSessionEnded: boolean; // True when session ended due to idle timeout
  connect: (storageKey?: string, onOpen?: () => void, onMessage?: (data: any) => void, onClose?: () => void, onError?: (error: Event) => void) => Promise<void>;
  disconnect: () => void;
  sendMessage: (payload: object) => void;
  sendAudio: (blob: Blob) => void;
  // Idle monitoring functions
  updateActivity: () => void;
  startIdleCheck: (timeoutMs?: number) => void;
  cancelIdleCheck: () => void;
  // Server message handling
  subscribeToServerMessage: (handler: (data: any) => void) => void;
  unsubscribeFromServerMessage: (handler: (data: any) => void) => void;
  // Connection health check
  startHealthCheck: (intervalMs?: number) => void;
  stopHealthCheck: () => void;
  isHealthy: boolean;
  // Reconnection
  reconnect: () => void;
  // Session restart
  restartSession: () => void;
}

// Create the context with a default undefined value
const SocketContext = createContext<SocketContextType | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
  sessionId?: string; // Allow users to provide a specific session ID
  storageKey?: string; // Key for storing session ID in localStorage
  alwaysCreateNew?: boolean; // Whether to always create a new session when sessionId is null (ignoring localStorage)
  onSessionCreated?: (id: string) => void; // Callback when a new session is created
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, sessionId: providedSessionId, storageKey = 'sid', alwaysCreateNew = false, onSessionCreated }) => {
  const [sessionId, setSessionId] = useState<string | null>(null); // Initialize to null on server
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isHealthy, setIsHealthy] = useState(true);
  const [isSessionEnded, setIsSessionEnded] = useState(false); // True when session ended due to idle
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  
  // Refs for idle management
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<number>(90000); // Default to 90 seconds

  // Ref for server message handlers
  const serverMessageHandlersRef = useRef<Set<(data: any) => void>>(new Set());

  // Ref for health check
  const healthCheckTimerRef = useRef<NodeJS.Timeout | null>(null);
  const healthCheckIntervalRef = useRef<number>(30000); // Default to 30 seconds

  // Refs for reconnection
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5; // Maximum reconnection attempts
  const reconnectInterval = 5000; // Reconnect interval in ms

  // Track if we've ever successfully connected (to detect invalid session IDs)
  const hasEverConnectedRef = useRef<boolean>(false);

  // Track if we're ending session due to idle timeout (to prevent auto-reconnect)
  const isIdleDisconnectRef = useRef<boolean>(false);

  // Ref for the WebSocket service instance
  const webSocketServiceRef = useRef<WebSocketService | null>(null);

  // Initialize or update session ID when props change (client-side only)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (providedSessionId) {
        setSessionId(providedSessionId);
      } else if (alwaysCreateNew) {
        // Don't use localStorage when alwaysCreateNew is true
        setSessionId(null);
      } else {
        const storedSessionId = window.localStorage.getItem(storageKey);
        setSessionId(storedSessionId);
      }
    }
  }, [providedSessionId, alwaysCreateNew, storageKey]);

  // Define all callback functions without dependencies that reference each other
  // to avoid circular dependencies

  const cancelIdleCheck = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const stopHealthCheck = useCallback(() => {
    if (healthCheckTimerRef.current) {
      clearInterval(healthCheckTimerRef.current);
      healthCheckTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (webSocketServiceRef.current) {
      webSocketServiceRef.current.close();
      webSocketServiceRef.current = null;
    }
    setIsConnected(false);
    setIsConnecting(false);
    setIsHealthy(false); // Connection is not healthy when disconnected
    cancelIdleCheck();
    stopHealthCheck();
  }, [cancelIdleCheck, stopHealthCheck]);

  const reconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log(`Max reconnection attempts (${maxReconnectAttempts}) reached.`);

      // If we've never successfully connected, the session ID is likely invalid
      // Clear localStorage and reset for a fresh session, then auto-retry
      if (!hasEverConnectedRef.current) {
        console.log('Never successfully connected - clearing potentially invalid session ID from localStorage');
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(storageKey);
        }
        setSessionId(null);
        reconnectAttemptsRef.current = 0;
        hasEverConnectedRef.current = false; // Reset for fresh connection

        // Auto-retry with fresh session after a short delay
        console.log('Auto-retrying with fresh session...');
        reconnectTimerRef.current = setTimeout(() => {
          setIsConnecting(true);
          setIsConnected(false);
          doConnect();
        }, 1000);
      }
      return;
    }

    if (isConnecting || isConnected) {
      console.log('Already connecting or connected, skipping reconnection attempt');
      return;
    }

    console.log(`Attempting to reconnect... (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

    // Clear any existing reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }

    // Set timer for reconnection
    reconnectTimerRef.current = setTimeout(() => {
      setIsConnecting(true);
      // Reset connection status before attempting to connect
      setIsConnected(false);
      // Use a direct reconnection function to avoid circular dependency
      doConnect();
      reconnectAttemptsRef.current++;
    }, reconnectInterval);
  }, [isConnecting, isConnected, storageKey]);

  const subscribeToServerMessage = useCallback((handler: (data: any) => void) => {
    serverMessageHandlersRef.current.add(handler);
  }, []);

  const unsubscribeFromServerMessage = useCallback((handler: (data: any) => void) => {
    serverMessageHandlersRef.current.delete(handler);
  }, []);

  // Internal function to handle connection without useCallback to avoid circular dependency
  const doConnect = async (
    storageKeyParam?: string,
    onOpen?: () => void, 
    onMessage?: (data: any) => void, 
    onClose?: () => void, 
    onError?: (error: Event) => void
  ) => {
    try {
      setIsConnecting(true);
      
      // Create a new WebSocket service instance for this provider
      const service = new WebSocketService();
      webSocketServiceRef.current = service;
      
      // Get session ID if not already available
      let currentSessionId = sessionId || providedSessionId;
      if (!currentSessionId) {
        let newSessionId;
        
        if (alwaysCreateNew) {
          // Always create a new session ID
          const newSession = await fetchNewSessionId();
          if (!newSession) {
            throw new Error('Failed to obtain new session.');
          }
          newSessionId = newSession.sessionId;
        } else {
          // Get session normally (may reuse existing)
          const session = await getSession(storageKeyParam || storageKey);
          if (!session) {
            throw new Error('Failed to obtain session.');
          }
          newSessionId = session.sessionId;
        }
        
        currentSessionId = newSessionId;
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(storageKeyParam || storageKey, currentSessionId);
        }
        setSessionId(currentSessionId);
        
        // Call onSessionCreated callback if provided
        if (onSessionCreated) {
          onSessionCreated(currentSessionId);
        }
      }

      // Add event listeners to the service
      if (onOpen) service.on('open', onOpen);
      if (onMessage) service.on('message', onMessage);
      if (onClose) service.on('close', onClose);
      if (onError) service.on('error', onError);

      // Listen for connection state changes
      service.on('open', () => {
        setIsConnected(true);
        setIsConnecting(false);
        setIsHealthy(true); // Reset health status on connection
        reconnectAttemptsRef.current = 0; // Reset reconnection attempts on successful connection
        hasEverConnectedRef.current = true; // Mark that we've successfully connected at least once
        updateActivity(); // Update activity on connection
        startIdleCheck(); // Start idle check on connection
        startHealthCheck(); // Start health check on connection
      });

      service.on('message', (data: any) => {
        // Update activity on receiving any message
        updateActivity();
        // Also notify all registered server message handlers
        serverMessageHandlersRef.current.forEach(handler => handler(data));
      });

      service.on('close', (event: any) => {
        console.log(`WebSocket closed: ${event?.code} - ${event?.reason}`);
        setIsConnected(false);
        setIsConnecting(false);
        setIsHealthy(false); // Connection is not healthy when closed
        cancelIdleCheck(); // Cancel idle check on disconnection
        stopHealthCheck(); // Stop health check on disconnection

        // Don't reconnect if this was an intentional idle disconnect
        if (isIdleDisconnectRef.current) {
          console.log('Idle disconnect - not attempting to reconnect');
          isIdleDisconnectRef.current = false; // Reset the flag
          return;
        }

        // Attempt to reconnect if the connection was closed unexpectedly
        // (codes 1000 and 1001 are normal closures, others might indicate errors)
        if (event?.code !== 1000 && event?.code !== 1001) {
          reconnect();
        } else {
          // For normal closures, reset reconnection attempts
          reconnectAttemptsRef.current = 0;
        }
      });

      service.on('error', (error: any) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        setIsConnecting(false);
        setIsHealthy(false); // Connection is not healthy when there's an error
        cancelIdleCheck(); // Cancel idle check on error
        stopHealthCheck(); // Stop health check on error
        
        // Attempt to reconnect on error
        reconnect();
      });

      service.on('reconnecting', (reconnectData: any) => {
        console.log(`Attempting to reconnect: ${reconnectData?.attempt}/${service.MAX_RECONNECT_ATTEMPTS}`);
      });

      // Initialize the WebSocket connection
      service.initialize(currentSessionId);
    } catch (error) {
      console.error('Connection error:', error);
      setIsConnecting(false);
      setIsHealthy(false);
    }
  };

  const connect = useCallback(doConnect, [sessionId, providedSessionId, storageKey]);

  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    // If idle timer is running, clear and restart it with proper session-end logic
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        console.log('Socket idle timeout reached - sending idle_timeout to server');
        // Mark this as an idle disconnect to prevent auto-reconnect
        isIdleDisconnectRef.current = true;
        // Send idle_timeout action to server so it can send goodbye message
        if (webSocketServiceRef.current) {
          webSocketServiceRef.current.send({ action: 'idle_timeout' });
        }
        // Wait a moment for the server to send goodbye message, then disconnect
        setTimeout(() => {
          setIsSessionEnded(true);
          disconnect();
          // Prevent auto-reconnect
          reconnectAttemptsRef.current = maxReconnectAttempts;
        }, 1000);
      }, idleTimeoutRef.current);
    }
  }, [disconnect]);

  const startIdleCheck = useCallback((timeoutMs: number = 90000) => {
    idleTimeoutRef.current = timeoutMs;
    // Clear any existing timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    // Set new timer
    idleTimerRef.current = setTimeout(() => {
      console.log('Socket idle timeout reached - sending idle_timeout to server');
      // Mark this as an idle disconnect to prevent auto-reconnect
      isIdleDisconnectRef.current = true;
      // Send idle_timeout action to server so it can send goodbye message
      if (webSocketServiceRef.current) {
        webSocketServiceRef.current.send({ action: 'idle_timeout' });
      }
      // Wait a moment for the server to send goodbye message, then disconnect
      setTimeout(() => {
        setIsSessionEnded(true);
        disconnect();
        // Reset reconnection attempts - we don't want to auto-reconnect
        reconnectAttemptsRef.current = maxReconnectAttempts; // Prevent auto-reconnect
      }, 1000); // Give server 1 second to send goodbye message
    }, timeoutMs);
  }, [disconnect]);

  const startHealthCheck = useCallback((intervalMs: number = 30000) => {
    healthCheckIntervalRef.current = intervalMs;
    
    // Clear any existing timer
    if (healthCheckTimerRef.current) {
      clearInterval(healthCheckTimerRef.current);
    }
    
    // Set new interval for health check
    healthCheckTimerRef.current = setInterval(() => {
      // Check if we've had recent activity
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;
      
      if (timeSinceLastActivity > intervalMs * 2) { // If no activity for 2x the interval time
        console.log('No recent activity detected - health check failed');
        setIsHealthy(false);
        // For health check failures, trigger a reconnection attempt
        if (isConnected) {
          console.log('Initiating reconnection due to health check failure');
          reconnect();
        }
      } else {
        setIsHealthy(true);
      }
    }, intervalMs);
  }, [lastActivity, isConnected, reconnect]);

  const sendMessage = useCallback((payload: object) => {
    if (isConnected && webSocketServiceRef.current) {
      updateActivity(); // Update activity when sending message
      webSocketServiceRef.current.send(payload);
    } else {
      console.warn('Cannot send message: WebSocket not connected');
    }
  }, [isConnected, updateActivity]);

  const sendAudio = useCallback((blob: Blob) => {
    if (isConnected && webSocketServiceRef.current) {
      updateActivity(); // Update activity when sending audio
      webSocketServiceRef.current.send(blob);
    } else {
      console.warn('Cannot send audio: WebSocket not connected');
    }
  }, [isConnected, updateActivity]);

  // Restart session by reloading the page (temporary solution until we have session persistence)
  const restartSession = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Disconnect and cleanup on unmount
      if (webSocketServiceRef.current) {
        webSocketServiceRef.current.close();
        webSocketServiceRef.current = null;
      }
      cancelIdleCheck();
      stopHealthCheck();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [cancelIdleCheck, stopHealthCheck]);

  const value = {
    sessionId,
    isConnected,
    isConnecting,
    isHealthy,
    isSessionEnded,
    connect,
    disconnect,
    sendMessage,
    sendAudio,
    // Idle monitoring functions
    updateActivity,
    startIdleCheck,
    cancelIdleCheck,
    // Server message handling
    subscribeToServerMessage,
    unsubscribeFromServerMessage,
    // Connection health check
    startHealthCheck,
    stopHealthCheck,
    // Reconnection
    reconnect,
    // Session restart
    restartSession,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};